// Hooks TanStack Query do delegation log (Parte 5.7, 14; REQ-AUTHZ-006..008/010/022; GIY-075) sobre o cliente tipado. As chaves levam o tenant E o estudo —
// `['authz', 'tenant', tenantId, 'delegations', studySiteId, …]` — para que a troca de contexto (regra 6 de 14.7) e a mudança de estudo nunca misturem dados de um
// escopo em outro; as da própria pessoa ficam em `['authz', 'me', 'delegations', …]`. As mutações são `silent`: cada diálogo mostra o erro inline (o 423 das
// travas traz `blockingConstraints[]`). Assinar, aceitar e revogar exigem step-up (A.1: U) e NUNCA são repetidas sozinhas depois do redirect; as rotas de
// delegação não usam `If-Match` (o contrato não o pede: o estado é conferido no servidor sob a trava do estudo) — o `ETag` da delegação vem de `GET …/{id}`.
import { type components } from '@giyus/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useApiClient } from '../../../lib/api/apiClientContext';
import { downloadBlob, meKey, tenantKey } from './queries';

type Schemas = components['schemas'];

export type Delegation = Schemas['AuthorizationDelegationResponse'];
export type DelegationLog = Schemas['AuthorizationDelegationLogResponse'];
export type DelegationTask = Schemas['AuthorizationDelegationTaskResponse'];
export type DelegationSignature = Schemas['AuthorizationDelegationSignatureResponse'];
export type DelegationTaskType = Schemas['AuthorizationDelegationTaskTypeResponse'];
export type DelegationBlockAttempt = Schemas['AuthorizationDelegationBlockAttemptResponse'];
export type DelegationBlockReason = Schemas['AuthorizationDelegationBlockReasonResponse'];
export type MyDelegation = Schemas['AuthorizationMyDelegationResponse'];
export type SaveDelegationInput = Schemas['AuthorizationCreateDelegationRequest'];
export type AmendDelegationInput = Schemas['AuthorizationAmendDelegationRequest'];
export type SignDelegationInput = Schemas['AuthorizationSignDelegationRequest'];

export const delegationsKey = (tenantId: string, studySiteId: string) =>
  [...tenantKey(tenantId), 'delegations', studySiteId] as const;
export const myDelegationsKey = [...meKey, 'delegations'] as const;

function idempotency(): { 'Idempotency-Key': string } {
  return { 'Idempotency-Key': crypto.randomUUID() };
}

function required<T>(data: T | undefined): T {
  if (data === undefined) {
    throw new Error('Empty response');
  }
  return data;
}

export interface StudyRef {
  readonly tenantId: string;
  readonly studySiteId: string;
}

// ---- Leituras ----

/** O delegation log do estudo: versão corrente + histórico. `enabled: false` quando o papel não lê o log (o servidor responderia 403). */
export function useDelegationLog(study: StudyRef, enabled = true) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...delegationsKey(study.tenantId, study.studySiteId), 'log'],
    queryFn: async (): Promise<DelegationLog> => {
      const { data } = await client.GET(
        '/api/v1/tenants/{tenantId}/studies/{studySiteId}/delegation-log',
        { params: { path: study } },
      );
      return required(data);
    },
    enabled,
    meta: { silent: true },
  });
}

/** Catálogo de tarefas delegáveis (Anexo A.4). */
export function useDelegationTaskTypes(study: StudyRef, enabled = true) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...delegationsKey(study.tenantId, study.studySiteId), 'task-types'],
    queryFn: async (): Promise<DelegationTaskType[]> => {
      const { data } = await client.GET(
        '/api/v1/tenants/{tenantId}/studies/{studySiteId}/delegation-log/task-types',
        { params: { path: study } },
      );
      return required(data);
    },
    enabled,
    staleTime: 60_000,
    meta: { silent: true },
  });
}

/** Tentativas de delegação bloqueadas pelas travas (REQ-AUTHZ-007), da mais nova à mais antiga. */
export function useDelegationBlockAttempts(study: StudyRef, enabled = true) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...delegationsKey(study.tenantId, study.studySiteId), 'block-attempts'],
    queryFn: async (): Promise<DelegationBlockAttempt[]> => {
      const { data } = await client.GET(
        '/api/v1/tenants/{tenantId}/studies/{studySiteId}/delegation-log/block-attempts',
        { params: { path: study } },
      );
      return required(data);
    },
    enabled,
    meta: { silent: true },
  });
}

/** As delegações da própria pessoa que o PI já assinou (`GET /me/delegations`), opcionalmente por situação. */
export function useMyDelegations(status?: string) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...myDelegationsKey, status ?? 'all'],
    queryFn: async (): Promise<MyDelegation[]> => {
      const { data } = await client.GET('/api/v1/me/delegations', {
        params: { query: status ? { status } : {} },
      });
      return required(data);
    },
    meta: { silent: true },
  });
}

// ---- Mutações ----

function useInvalidateStudy(study: StudyRef) {
  const queryClient = useQueryClient();
  return async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: delegationsKey(study.tenantId, study.studySiteId),
      }),
      queryClient.invalidateQueries({ queryKey: myDelegationsKey }),
    ]);
  };
}

/** Rascunho (`POST …/delegations`): o 423 das travas também grava uma tentativa — a lista de tentativas é invalidada em qualquer desfecho. */
export function useCreateDelegation(study: StudyRef) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const invalidate = useInvalidateStudy(study);
  return useMutation({
    mutationFn: async (input: SaveDelegationInput): Promise<Delegation> => {
      const { data } = await client.POST(
        '/api/v1/tenants/{tenantId}/studies/{studySiteId}/delegation-log/delegations',
        { params: { path: study, header: idempotency() }, body: input },
      );
      return required(data);
    },
    onSuccess: invalidate,
    onError: async () => {
      await queryClient.invalidateQueries({
        queryKey: [...delegationsKey(study.tenantId, study.studySiteId), 'block-attempts'],
      });
    },
    meta: { silent: true },
  });
}

/** Emenda: nova versão em rascunho a partir da ativa (`POST …/{id}/amend`); a anterior segue em vigor até o aceite. */
export function useAmendDelegation(study: StudyRef) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const invalidate = useInvalidateStudy(study);
  return useMutation({
    mutationFn: async (args: {
      readonly delegationId: string;
      readonly input: AmendDelegationInput;
    }): Promise<Delegation> => {
      const { data } = await client.POST(
        '/api/v1/tenants/{tenantId}/studies/{studySiteId}/delegation-log/delegations/{delegationId}/amend',
        {
          params: { path: { ...study, delegationId: args.delegationId }, header: idempotency() },
          body: args.input,
        },
      );
      return required(data);
    },
    onSuccess: invalidate,
    onError: async () => {
      await queryClient.invalidateQueries({
        queryKey: [...delegationsKey(study.tenantId, study.studySiteId), 'block-attempts'],
      });
    },
    meta: { silent: true },
  });
}

/** Assinatura do PI (`POST …/{id}/pi-sign`, step-up): o hash do conteúdo que ele viu e um comentário opcional. */
export function usePiSignDelegation(study: StudyRef) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const invalidate = useInvalidateStudy(study);
  return useMutation({
    mutationFn: async (args: {
      readonly delegationId: string;
      readonly input: SignDelegationInput;
    }): Promise<Delegation> => {
      const { data } = await client.POST(
        '/api/v1/tenants/{tenantId}/studies/{studySiteId}/delegation-log/delegations/{delegationId}/pi-sign',
        {
          params: { path: { ...study, delegationId: args.delegationId }, header: idempotency() },
          body: args.input,
        },
      );
      return required(data);
    },
    onSuccess: invalidate,
    onError: async () => {
      await queryClient.invalidateQueries({
        queryKey: [...delegationsKey(study.tenantId, study.studySiteId), 'block-attempts'],
      });
    },
    meta: { silent: true },
  });
}

/** Revogação pelo PI com razão (`POST …/{id}/revoke`, step-up). */
export function useRevokeDelegation(study: StudyRef) {
  const client = useApiClient();
  const invalidate = useInvalidateStudy(study);
  return useMutation({
    mutationFn: async (args: {
      readonly delegationId: string;
      readonly reason: string;
    }): Promise<Delegation> => {
      const { data } = await client.POST(
        '/api/v1/tenants/{tenantId}/studies/{studySiteId}/delegation-log/delegations/{delegationId}/revoke',
        {
          params: { path: { ...study, delegationId: args.delegationId }, header: idempotency() },
          body: { reason: args.reason },
        },
      );
      return required(data);
    },
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

/** Aceite pela própria pessoa (`POST /me/delegations/{id}/accept`, step-up): invalida as dela e o log do estudo (agora acessível). */
export function useAcceptDelegation() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      readonly delegationId: string;
      readonly input: SignDelegationInput;
    }): Promise<MyDelegation> => {
      const { data } = await client.POST('/api/v1/me/delegations/{delegationId}/accept', {
        params: { path: { delegationId: args.delegationId }, header: idempotency() },
        body: args.input,
      });
      return required(data);
    },
    onSuccess: async (accepted) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: myDelegationsKey }),
        queryClient.invalidateQueries({
          queryKey: [...tenantKey(accepted.tenantId), 'delegations'],
        }),
      ]);
    },
    onError: async () => {
      await queryClient.invalidateQueries({ queryKey: myDelegationsKey });
    },
    meta: { silent: true },
  });
}

/**
 * Exportação do log em PDF (`GET …/export.pdf?locale=`): devolve o arquivo e o nome sugerido. Sem o módulo de Documentos o servidor responde
 * `423 AUTHZ_DELEGATION_EXPORT_NOT_AVAILABLE` (OPEN-120) — a tela explica em vez de falhar.
 */
export function useExportDelegationLog(study: StudyRef) {
  const client = useApiClient();
  return useMutation({
    mutationFn: async (locale: string): Promise<{ blob: Blob; fileName: string }> => {
      const result = await client.GET(
        '/api/v1/tenants/{tenantId}/studies/{studySiteId}/delegation-log/export.pdf',
        { params: { path: study, query: { locale } }, parseAs: 'blob' },
      );
      const blob = result.data;
      if (!blob) {
        throw new Error('Empty export');
      }
      const disposition = result.response.headers.get('Content-Disposition') ?? '';
      const match = /filename="?([^";]+)"?/.exec(disposition);
      return { blob, fileName: match?.[1] ?? 'delegation-log.pdf' };
    },
    meta: { silent: true },
  });
}

export { downloadBlob };
